import React, { useState } from "react";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import Button from "@mui/material/Button";
import Box from "@mui/material/Box";
import CircularProgress from "@mui/material/CircularProgress";
import Alert from "@mui/material/Alert";
import Typography from "@mui/material/Typography";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";

import { deleteOrderItem, updateOrderItem } from "../../../../../services/Admin/OrdersApi";

const toSafeNumber = (value) => {
	const parsed = Number(String(value ?? "").replace(/,/g, "").trim());
	return Number.isFinite(parsed) ? parsed : 0;
};

const DeleteItemsDialog = ({
	open,
	onClose,
	item,
	onDeleted,
	canDeleteItem = true,
	blockedMessage = "ليس لديك صلاحية حذف هذا الصنف.",
	itemTotalValue,
	itemUnitValue,
}) => {
	const [submitting, setSubmitting] = useState(false);
	const [error, setError] = useState("");

	const currentQuantity = toSafeNumber(item?.quantity);
	const nextQuantity = Math.max(currentQuantity - 1, 0);
	const willReduceQuantityOnly = currentQuantity > 1;

	const handleDelete = async () => {
		setError("");

		if (!canDeleteItem) {
			setError(blockedMessage);
			return;
		}

		// Validation
		if (!item?.id) {
			setError("معرف العنصر مطلوب");
			return;
		}

		setSubmitting(true);
		try {
			const res = willReduceQuantityOnly
				? await updateOrderItem({
					id: item.id,
					quantity: nextQuantity,
					note: item.note || "",
					status: item.status || "pending",
					open_time: item.open_time || null,
					close_time: item.close_time || null,
					price: toSafeNumber(item.price) || toSafeNumber(itemUnitValue),
				})
				: await deleteOrderItem(item.id);

			// Success: notify parent and close
			onDeleted && onDeleted(res?.data || null);
			onClose();
		} catch (err) {
			setError(err?.message || "فشل حذف الصنف");
		} finally {
			setSubmitting(false);
		}
	};

	return (
		<Dialog 
			open={Boolean(open)} 
			onClose={onClose} 
			maxWidth="xs" 
			fullWidth
		>
			<DialogTitle sx={{ display: "flex", alignItems: "center", gap: 1 }}>
				<WarningAmberIcon color="warning" />
				تأكيد الحذف
			</DialogTitle>

			<DialogContent dividers>
				{error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

				<Box sx={{ py: 2 }}>
					<Typography variant="body1" sx={{ mb: 2 }}>
						{willReduceQuantityOnly
							? "سيتم حذف عدد واحد فقط من هذا الصنف وتقليل الكمية."
							: "هل أنت متأكد من حذف هذا الصنف من الطلب؟"}
					</Typography>

					{item && (
						<Box 
							sx={{ 
								p: 2, 
								bgcolor: "grey.100", 
								borderRadius: 1,
								border: "1px solid",
								borderColor: "grey.300"
							}}
						>
							{item.product?.name && (
								<Typography variant="subtitle2" sx={{ mb: 0.5 }}>
									<strong>المنتج:</strong> {item.product.name}
								</Typography>
							)}
							{item.quantity && (
								<Typography variant="body2" sx={{ mb: 0.5 }}>
									<strong>الكمية:</strong> {item.quantity}
								</Typography>
							)}
							{willReduceQuantityOnly && (
								<Typography variant="body2" sx={{ mb: 0.5 }}>
									<strong>الكمية بعد الحذف:</strong> {nextQuantity}
								</Typography>
							)}
							{item.price && (
								<Typography variant="body2" sx={{ mb: 0.5 }}>
									<strong>السعر:</strong> {item.price} ج.م
								</Typography>
							)}
							{itemUnitValue !== undefined && (
								<Typography variant="body2" sx={{ mb: 0.5 }}>
									<strong>سعر الوحدة:</strong> {Number(itemUnitValue).toFixed(2)} ج.م
								</Typography>
							)}
							{itemTotalValue !== undefined && (
								<Typography variant="body2" sx={{ mb: 0.5 }}>
									<strong>إجمالي الصنف:</strong> {Number(itemTotalValue).toFixed(2)} ج.م
								</Typography>
							)}
							{item.note && (
								<Typography variant="body2">
									<strong>الملاحظات:</strong> {item.note}
								</Typography>
							)}
						</Box>
					)}

					<Typography 
						variant="body2" 
						color="error" 
						sx={{ mt: 2, fontWeight: "bold" }}
					>
						{willReduceQuantityOnly
							? "تحذير: سيتم تقليل الكمية بمقدار 1 فقط"
							: "تحذير: لا يمكن التراجع عن هذه العملية"}
					</Typography>
				</Box>
			</DialogContent>

			<DialogActions>
				<Button 
					onClick={onClose} 
					disabled={submitting}
					variant="outlined"
				>
					إلغاء
				</Button>
				<Button 
					onClick={handleDelete} 
					variant="contained" 
					color="error"
					disabled={submitting || !canDeleteItem}
				>
					{submitting ? <CircularProgress size={20} /> : (willReduceQuantityOnly ? "حذف عدد 1" : "حذف")}
				</Button>
			</DialogActions>
		</Dialog>
	);
};

export default DeleteItemsDialog;
